import React, { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  Form,
  Select,
  Input,
  Button,
  Space,
  Typography,
  Divider,
  InputNumber,
  DatePicker,
  Radio,
  Alert,
  Tag,
  Card,
} from 'antd';
import {
  PlusOutlined,
  DeleteOutlined,
  BranchesOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { selectTemplateData } from '../../../../store/slices/rfpTemplateSlice';

const { Text } = Typography;
const { Option } = Select;

const operators = [
  { label: 'Equals', value: '==', types: ['text', 'select', 'radio', 'number', 'email', 'phone', 'url', 'time'] },
  { label: 'Not Equals', value: '!=', types: ['text', 'select', 'radio', 'number', 'email', 'phone', 'url', 'time'] },
  { label: 'Contains', value: 'in', types: ['text', 'textarea', 'email', 'multiselect', 'checkbox'] },
  { label: 'Greater Than', value: '>', types: ['number', 'date', 'datetime', 'time', 'currency', 'percentage'] },
  { label: 'Less Than', value: '<', types: ['number', 'date', 'datetime', 'time', 'currency', 'percentage'] },
  { label: 'Greater or Equal', value: '>=', types: ['number', 'date', 'datetime', 'time', 'currency', 'percentage'] },
  { label: 'Less or Equal', value: '<=', types: ['number', 'date', 'datetime', 'time', 'currency', 'percentage'] },
  { label: 'Is Empty', value: 'isEmpty', types: ['text', 'textarea', 'select', 'multiselect', 'checkbox', 'daterange'] },
  { label: 'Is Not Empty', value: 'isNotEmpty', types: ['text', 'textarea', 'select', 'multiselect', 'checkbox', 'daterange'] },
];

// Helper to serialize values for JSON Logic (convert Dates to strings)
const serializeValue = (value) => {
  if (!value) return value;

  // Handle Ant Design Moment/Dayjs objects
  if (typeof value === 'object' && value.format) {
    return value.format('YYYY-MM-DD HH:mm:ss');
  }
  // Handle native Date objects
  if (value instanceof Date) {
    return value.toISOString();
  }
  // Handle arrays of dates
  if (Array.isArray(value)) {
    return value.map(serializeValue);
  }
  return value;
};

export default function ConditionalLogicBuilder({ open, onClose, onSave, initialRules, currentFieldPath }) {
  const [form] = Form.useForm();
  const templateData = useSelector(selectTemplateData);

  // Initialize rules from initialRules
  const initializeRules = () => {
    if (!initialRules) return [];
    if (initialRules.and) return initialRules.and.map(parseRule);
    if (initialRules.or) return initialRules.or.map(parseRule);
    return [];
  };

  const initializeOperator = () => {
    if (!initialRules) return 'and';
    if (initialRules.or) return 'or';
    return 'and';
  };

  // Parse a JSON Logic rule back to simple format
  const parseRule = (jsonRule) => {
    // Handle isEmpty (converted to ==)
    if (jsonRule['=='] && jsonRule['=='][1] === '') {
      return {
        field: jsonRule['=='][0].var,
        operator: 'isEmpty',
        value: '',
      };
    }

    // Handle isNotEmpty (converted to !=)
    if (jsonRule['!='] && jsonRule['!='][1] === '') {
      return {
        field: jsonRule['!='][0].var,
        operator: 'isNotEmpty',
        value: '',
      };
    }

    // Handle 'in' operator (parameters are swapped)
    if (jsonRule['in']) {
      return {
        field: jsonRule['in'][1].var,
        operator: 'in',
        value: jsonRule['in'][0],
      };
    }

    // Handle standard operators
    const operator = Object.keys(jsonRule)[0];
    const operands = jsonRule[operator];

    return {
      field: operands[0].var,
      operator: operator,
      value: operands[1],
    };
  };

  const [rules, setRules] = useState(initializeRules());
  const [logicOperator, setLogicOperator] = useState(initializeOperator());

  // Update state when initialRules prop changes (fixing sticky behavior)
  useEffect(() => {
    setRules(initializeRules());
    setLogicOperator(initializeOperator());
  }, [initialRules]);

  // Extract all fields from template with metadata
  const availableFields = useMemo(() => {
    if (!templateData?.sheets) {
      console.log('❌ No templateData.sheets');
      return [];
    }

    // Exclude complex field types from conditional logic
    const excludedFieldTypes = ['richtext', 'file', 'rating', 'user', 'location', 'table', 'repeater'];

    const fields = [];
    templateData.sheets.forEach((sheet, sheetIdx) => {
      sheet.sections?.forEach((section, sectionIdx) => {
        section.categories?.forEach((category, catIdx) => {
          category.questions?.forEach((question, qIdx) => {
            const fieldPath = `sheet-${sheetIdx}-section-${sectionIdx}-category-${catIdx}-question-${qIdx}`;

            // Skip current field (can't reference itself)
            if (fieldPath === currentFieldPath) return;

            // Skip complex field types
            if (excludedFieldTypes.includes(question.field_type)) return;

            fields.push({
              value: question.id || fieldPath,
              label: question.question || question.label || `Question ${qIdx + 1}`,
              fieldType: question.field_type,
              options: question.options || [],
              path: fieldPath,
            });
          });
        });
      });
    });

    console.log('✅ Available fields for dropdown:', fields.length, fields);
    return fields;
  }, [templateData, currentFieldPath]);

  const handleAddRule = () => {
    setRules([
      ...rules,
      {
        field: '',
        operator: '==',
        value: '',
      },
    ]);
  };

  const handleRemoveRule = (index) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const handleRuleChange = (index, key, value) => {
    const newRules = [...rules];
    newRules[index][key] = value;

    // Reset value when field changes (different field types need different inputs)
    if (key === 'field') {
      newRules[index].value = '';

      // Set default operator based on field type
      const field = availableFields.find((f) => f.value === value);
      if (field) {
        const validOps = operators.filter((op) => op.types.includes(field.fieldType));
        if (validOps.length > 0) {
          newRules[index].operator = validOps[0].value;
        }
      }
    }

    setRules(newRules);
  };

  const handleSave = () => {
    // Validate rules
    const invalidRules = rules.filter((rule) => !rule.field || !rule.operator);
    if (invalidRules.length > 0) {
      Modal.error({
        title: 'Invalid Rules',
        content: 'Please complete all rule fields before saving.',
      });
      return;
    }

    // Convert to JSON Logic format
    console.log('🚀 Converting rules to JSON Logic:', rules);
    const jsonLogic = {
      [logicOperator]: rules.map((rule) => {
        // Handle isEmpty/isNotEmpty operators
        if (rule.operator === 'isEmpty') {
          return { '==': [{ var: rule.field }, ''] };
        }
        if (rule.operator === 'isNotEmpty') {
          return { '!=': [{ var: rule.field }, ''] };
        }

        // Handle 'in' operator (for arrays) - parameters need to be swapped
        if (rule.operator === 'in') {
          console.log(`🔍 Creating 'in' rule:`, {
            field: rule.field,
            value: rule.value,
            serializedValue: serializeValue(rule.value)
          });
          const inRule = { 'in': [serializeValue(rule.value), { var: rule.field }] };
          console.log(`📋 Generated 'in' JSON Logic:`, inRule);
          return inRule;
        }

        return {
          [rule.operator]: [{ var: rule.field }, serializeValue(rule.value)],
        };
      }),
    };

    console.log('💾 Final JSON Logic being saved:', JSON.stringify(jsonLogic, null, 2));
    onSave(jsonLogic);
    onClose();
  };

  const renderValueInput = (rule, index) => {
    const field = availableFields.find((f) => f.value === rule.field);
    if (!field) {
      return (
        <Input
          placeholder="Select a field first"
          disabled
          style={{ width: '100%' }}
          size="middle"
        />
      );
    }

    // Don't show value input for isEmpty/isNotEmpty
    if (rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty') {
      return (
        <div style={{ padding: '8px', background: '#F3F4F6', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>No value needed</Text>
        </div>
      );
    }

    const fieldType = field.fieldType;

    // Select/Radio fields - show options as dropdown
    if (['select', 'radio'].includes(fieldType) && field.options.length > 0) {
      return (
        <Select
          style={{ width: '100%' }}
          placeholder="Select value"
          value={rule.value || undefined}
          onChange={(value) => handleRuleChange(index, 'value', value)}
          size="middle"
        >
          {field.options.map((opt, idx) => (
            <Option key={idx} value={opt.label || opt.value || opt}>
              {opt.label || opt}
            </Option>
          ))}
        </Select>
      );
    }

    // Multiselect/Checkbox fields - for conditional logic, select single value to check for
    if (['multiselect', 'checkbox'].includes(fieldType) && field.options.length > 0) {
      return (
        <Select
          style={{ width: '100%' }}
          placeholder="Select value to check for"
          value={rule.value || undefined}
          onChange={(value) => handleRuleChange(index, 'value', value)}
          size="middle"
        >
          {field.options.map((opt, idx) => (
            <Option key={idx} value={opt.label || opt.value || opt}>
              {opt.label || opt}
            </Option>
          ))}
        </Select>
      );
    }

    // Number fields
    if (['number', 'currency', 'percentage'].includes(fieldType)) {
      return (
        <InputNumber
          style={{ width: '100%' }}
          placeholder="Enter number"
          value={rule.value}
          onChange={(value) => handleRuleChange(index, 'value', value)}
          size="middle"
        />
      );
    }

    // Date fields
    if (['date', 'datetime'].includes(fieldType)) {
      return (
        <DatePicker
          style={{ width: '100%' }}
          showTime={fieldType === 'datetime'}
          placeholder="Select date"
          value={rule.value}
          onChange={(value) => handleRuleChange(index, 'value', value)}
          size="middle"
        />
      );
    }

    // Time field
    if (fieldType === 'time') {
      return (
        <DatePicker
          style={{ width: '100%' }}
          picker="time"
          placeholder="Select time"
          value={rule.value}
          onChange={(value) => handleRuleChange(index, 'value', value)}
          size="middle"
        />
      );
    }

    // Daterange field (only for isEmpty/isNotEmpty, no value input needed)
    if (fieldType === 'daterange') {
      return (
        <div style={{ padding: '8px', background: '#F3F4F6', borderRadius: '6px', border: '1px solid #E5E7EB' }}>
          <Text type="secondary" style={{ fontSize: '13px' }}>No value needed for daterange</Text>
        </div>
      );
    }

    // Default: text input
    return (
      <Input
        placeholder="Enter value"
        value={rule.value}
        onChange={(e) => handleRuleChange(index, 'value', e.target.value)}
        style={{ width: '100%' }}
        size="middle"
      />
    );
  };

  const renderHumanReadablePreview = () => {
    if (rules.length === 0) {
      return (
        <Alert
          type="info"
          message="Field will always be visible"
          description="Add rules to control when this field appears."
          showIcon
        />
      );
    }

    const ruleDescriptions = rules.map((rule, index) => {
      const field = availableFields.find((f) => f.value === rule.field);
      const fieldLabel = field?.label || 'Unknown field';
      const operator = operators.find((op) => op.value === rule.operator);
      const operatorLabel = operator?.label || rule.operator;

      let valueDisplay = rule.value;

      // Handle isEmpty/isNotEmpty operators
      if (rule.operator === 'isEmpty' || rule.operator === 'isNotEmpty') {
        valueDisplay = '';
      }
      // Handle arrays (multiselect, checkbox)
      else if (Array.isArray(rule.value)) {
        valueDisplay = rule.value.join(', ');
      }
      // Handle Date objects (from DatePicker)
      else if (rule.value && typeof rule.value === 'object' && rule.value.format) {
        // Ant Design Moment/Dayjs object
        valueDisplay = rule.value.format('YYYY-MM-DD HH:mm');
      }
      else if (rule.value instanceof Date) {
        valueDisplay = rule.value.toLocaleDateString();
      }
      // Handle other objects
      else if (typeof rule.value === 'object' && rule.value !== null) {
        valueDisplay = JSON.stringify(rule.value);
      }

      return (
        <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          {index > 0 && (
            <Tag color="purple" style={{ marginRight: '8px' }}>
              {logicOperator.toUpperCase()}
            </Tag>
          )}
          <Text>
            <Text strong style={{ color: '#8B5CF6' }}>{fieldLabel}</Text>
            {' '}<Text type="secondary">{operatorLabel.toLowerCase()}</Text>{' '}
            {valueDisplay && <Text strong>"{valueDisplay}"</Text>}
          </Text>
        </div>
      );
    });

    return (
      <Card
        size="small"
        style={{ background: '#F9F5FF', border: '1px solid #E9D5FF' }}
      >
        <Space direction="vertical" size="small" style={{ width: '100%' }}>
          <Space>
            <CheckCircleOutlined style={{ color: '#8B5CF6' }} />
            <Text strong>Show this field when:</Text>
          </Space>
          {ruleDescriptions}
        </Space>
      </Card>
    );
  };

  return (
    <Modal
      title={
        <Space>
          <BranchesOutlined style={{ color: '#8B5CF6' }} />
          <span>Conditional Logic</span>
        </Space>
      }
      open={open}
      onCancel={onClose}
      onOk={handleSave}
      width={800}
      okText="Save Rules"
      okButtonProps={{ icon: <CheckCircleOutlined /> }}
      destroyOnClose={false}
      maskClosable={false}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <Alert
          type="info"
          message="Control when this field appears"
          description="Define conditions based on other fields' values. The field will only be shown when all conditions are met."
          showIcon
          closable
        />

        {rules.length > 1 && (
          <div>
            <Text type="secondary" style={{ display: 'block', marginBottom: '8px', fontSize: '13px' }}>
              Logic Operator
            </Text>
            <Radio.Group
              value={logicOperator}
              onChange={(e) => setLogicOperator(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="and">ALL conditions must match (AND)</Radio.Button>
              <Radio.Button value="or">ANY condition can match (OR)</Radio.Button>
            </Radio.Group>
          </div>
        )}

        {rules.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <BranchesOutlined style={{ fontSize: '48px', color: '#D1D5DB', marginBottom: '16px' }} />
            <div>
              <Text type="secondary">No rules defined</Text>
              <div style={{ marginTop: '4px' }}>
                <Text type="secondary" style={{ fontSize: '13px' }}>
                  Field will always be visible
                </Text>
              </div>
            </div>
          </div>
        ) : (
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            {rules.map((rule, index) => {
              const field = availableFields.find((f) => f.value === rule.field);
              const validOperators = field
                ? operators.filter((op) => op.types.includes(field.fieldType))
                : operators;

              return (
                <Card
                  key={index}
                  size="small"
                  style={{
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                  }}
                >
                  <Space style={{ width: '100%' }} direction="vertical" size="middle">
                    {index > 0 && (
                      <div style={{ marginBottom: '8px' }}>
                        <Tag color="purple">{logicOperator.toUpperCase()}</Tag>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end' }}>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                          When Field
                        </Text>
                        <Select
                          style={{ width: '100%' }}
                          placeholder="Select field"
                          value={rule.field || undefined}
                          onChange={(value) => {
                            console.log('🔵 Select onChange triggered, value:', value);
                            handleRuleChange(index, 'field', value);
                          }}
                          onSelect={(value) => {
                            console.log('🔵 onSelect triggered, value:', value);
                          }}
                          onOpenChange={(open) => {
                            console.log('🔵 Dropdown open changed:', open, 'Options count:', availableFields.length);
                          }}
                          onClick={() => console.log('🔵 Select clicked')}
                          showSearch
                          optionFilterProp="children"
                          filterOption={(input, option) =>
                            option.children.toLowerCase().includes(input.toLowerCase())
                          }
                          size="middle"
                        >
                          {availableFields.map(field => (
                            <Option key={field.value} value={field.value}>
                              {field.label}
                            </Option>
                          ))}
                        </Select>
                      </div>

                      <div style={{ width: '160px', flexShrink: 0 }}>
                        <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                          Condition
                        </Text>
                        <Select
                          style={{ width: '100%' }}
                          value={rule.operator}
                          onChange={(value) => handleRuleChange(index, 'operator', value)}
                          size="middle"
                        >
                          {validOperators.map(op => (
                            <Option key={op.value} value={op.value}>
                              {op.label}
                            </Option>
                          ))}
                        </Select>
                      </div>

                      <div style={{ flex: 1, minWidth: 0 }}>
                        <Text strong style={{ fontSize: '13px', display: 'block', marginBottom: '6px' }}>
                          Value
                        </Text>
                        {renderValueInput(rule, index)}
                      </div>

                      <Button
                        icon={<DeleteOutlined />}
                        danger
                        size="small"
                        onClick={() => handleRemoveRule(index)}
                        style={{ marginBottom: 0 }}
                      />
                    </div>
                  </Space>
                </Card>
              );
            })}
          </Space>
        )}

        <Button
          type="dashed"
          icon={<PlusOutlined />}
          onClick={handleAddRule}
          block
          size="large"
        >
          Add Condition
        </Button>

        <Divider />

        <div>
          <Text strong style={{ display: 'block', marginBottom: '12px' }}>
            Preview
          </Text>
          {renderHumanReadablePreview()}
        </div>

        <Divider />

        <div style={{ background: '#F9FAFB', padding: '12px', borderRadius: '8px', border: '1px solid #E5E7EB' }}>
          <Text strong style={{ fontSize: '13px' }}>
            JSON Logic Output:
          </Text>
          <pre style={{
            marginTop: '8px',
            fontSize: '11px',
            background: '#fff',
            padding: '12px',
            borderRadius: '6px',
            overflow: 'auto',
            maxHeight: '200px',
          }}>
            {JSON.stringify(
              {
                [logicOperator]: rules.map((rule) => {
                  if (rule.operator === 'isEmpty') {
                    return { '==': [{ var: rule.field }, ''] };
                  }
                  if (rule.operator === 'isNotEmpty') {
                    return { '!=': [{ var: rule.field }, ''] };
                  }
                  return {
                    [rule.operator]: [{ var: rule.field }, serializeValue(rule.value)],
                  };
                }),
              },
              null,
              2
            )}
          </pre>
        </div>
      </Space>
    </Modal>
  );
}
